import { useState, useEffect, useCallback } from 'react'
import { marketDataService } from '../services/marketDataService'
import { StockSearchResult, StockQuote } from '../types/investment'

interface UseStockSearchReturn {
  searchQuery: string
  searchResults: StockSearchResult[]
  selectedStock: StockQuote | null
  loading: boolean
  error: string | null
  setSearchQuery: (query: string) => void
  selectStock: (symbol: string) => Promise<void>
  clearSearch: () => void
}

export const useStockSearch = (): UseStockSearchReturn => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<StockSearchResult[]>([])
  const [selectedStock, setSelectedStock] = useState<StockQuote | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setSearchResults([])
        return
      }

      try {
        setLoading(true)
        setError(null)
        const results = await marketDataService.searchStocks(query)
        setSearchResults(results)
      } catch (err) {
        console.error('Error searching stocks:', err)
        setError(err instanceof Error ? err.message : 'Failed to search stocks')
        setSearchResults([])
      } finally {
        setLoading(false)
      }
    }, 300),
    []
  )

  // Effect to trigger search when query changes
  useEffect(() => {
    debouncedSearch(searchQuery)
  }, [searchQuery, debouncedSearch])

  const selectStock = useCallback(async (symbol: string) => {
    try {
      setLoading(true)
      setError(null)
      const quote = await marketDataService.fetchStockQuote(symbol)
      setSelectedStock(quote)
      setSearchQuery('') // Clear search after selection
      setSearchResults([])
    } catch (err) {
      console.error('Error fetching stock quote:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch stock quote')
    } finally {
      setLoading(false)
    }
  }, [])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setSearchResults([])
    setSelectedStock(null)
    setError(null)
  }, [])

  return {
    searchQuery,
    searchResults,
    selectedStock,
    loading,
    error,
    setSearchQuery,
    selectStock,
    clearSearch
  }
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), wait)
  }
}
